import React, { useState } from 'react';
import { IconButton } from 'rsuite';
import { Icon } from '@rsuite/icons';
import { FaFileExport } from 'react-icons/fa';
import { useStoreState } from '../../store/hooks';
import { exportAvailabilityUpdate, exportPricingData, mapToSnapshotRequest } from '../../api/pricing/pricing';
import { downloadData } from '../../utils/downloadData';
import { XLSX_MIME_TYPE } from '../../services/excel/constants';
import handleError from '../../api/apiErrorHandle';
import Alert from '../Alert';

export enum PricingExportType {
  PRICES_UPDATE = 'PRICES_UPDATE',
  PRICING_DATA = 'PRICING_DATA',
}

interface Props {
  type: PricingExportType;
}

export default function PricingExport({ type }: Readonly<Props>) {
  const currentFilter = useStoreState(state => state.pricingData.currentFilter);
  const totalItems = useStoreState(state => state.pricingData.total);
  const maxPricingRequestId = useStoreState(state => state.pricingData.maxPricingRequestId);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  async function handleExport() {
    setExporting(true);
    const request = mapToSnapshotRequest(currentFilter.filter, maxPricingRequestId);
    try {
      if (type === PricingExportType.PRICES_UPDATE) {
        const response = await exportAvailabilityUpdate(request);
        downloadData(response.data as string, 'text/plain', 'availability_update.csv');
      } else {
        const response = await exportPricingData(request);
        downloadData(
          response,
          XLSX_MIME_TYPE,
          'pricing_data.xlsx',
        );
      }
    } catch (error) {
      handleError(error);
    } finally {
      setExporting(false);
      setConfirmOpen(false);
    }
  }

  const label = type === PricingExportType.PRICES_UPDATE ? 'Price Update Export' : 'Pricing Data Export';

  return (
    <>
      <IconButton
        style={{ width: 220 }}
        appearance="primary"
        icon={<Icon as={FaFileExport} />}
        placement="right"
        onClick={() => setConfirmOpen(true)}
      >
        {label}
      </IconButton>

      <Alert
        isOpen={confirmOpen}
        isLoading={exporting}
        onConfirm={() => handleExport()}
        onClose={() => setConfirmOpen(false)}
        title="Confirmation"
      >
        <p>
          Are you sure you want to export
          {' '}
          <b>{totalItems}</b>
          {' '}
          items?
        </p>
      </Alert>
    </>
  );
}
